import {
  Avatar,
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  type ChipProps,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Reply as ReplyIcon,
  ReplyAll as ReplyAllIcon,
} from '@mui/icons-material';
import { FinanceEmailAttachmentsField } from '../detail-form/FinanceEmailAttachmentsField';
import { isInboundFinanceEmailHistoryId } from '../grid/utils/financeEmailHistoryId';
import { FinanceEmailCollapsibleBody } from './FinanceEmailCollapsibleBody';
import type { FinanceEmailInboxMessage } from './types';
import { canReplyAllToMessage } from './utils/canReplyAll';
import {
  avatarColor,
  formatMessageDate,
  formatOutboundDeliveryStatusLabel,
  getOutboundDeliveryStatusTone,
  initials,
  sanitizeEmailHtml,
  snippetFromBody,
} from './utils/threadDisplayUtils';

type FinanceEmailMessageCardProps = {
  message: FinanceEmailInboxMessage;
  expanded: boolean;
  onToggle: () => void;
  isLatest: boolean;
  onReply: (historyId: string, replyAll?: boolean) => void;
  isReplying?: boolean;
};

export const FinanceEmailMessageCard = ({
  message,
  expanded,
  onToggle,
  isLatest,
  onReply,
  isReplying,
}: FinanceEmailMessageCardProps) => {
  const isInbound = (message.direction || '').toLowerCase() === 'inbound';
  const canReply = isInbound && isInboundFinanceEmailHistoryId(message.historyId);
  const canReplyAll = canReply && canReplyAllToMessage(message);
  // Outbound: show the finance mailbox From address, not the HRM user who clicked Send.
  const who = isInbound
    ? message.fromAddress || message.clientName || 'Sender'
    : message.fromAddress || message.sentBy || 'Finance';
  const unread = isInbound && !message.isHandled;
  const sanitizedBody = sanitizeEmailHtml(message.body) || '<p><em>(empty message)</em></p>';
  const statusLabel = isInbound ? 'Received' : formatOutboundDeliveryStatusLabel(message.status);
  const statusTone = isInbound ? 'default' : getOutboundDeliveryStatusTone(message.status);
  const statusColor: ChipProps['color'] = statusTone === 'default' ? 'default' : statusTone;
  const ccRecipients = (message.ccRecipients || '').trim();
  const bccRecipients = (message.bccRecipients || '').trim();

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: expanded && isLatest ? 'primary.light' : 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: expanded ? '0 1px 2px rgba(0,0,0,0.04)' : 'none',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
        sx={{
          px: 2,
          py: expanded ? 1.5 : 1.1,
          cursor: 'pointer',
          '&:hover': { bgcolor: expanded ? 'transparent' : 'action.hover' },
        }}
        onClick={onToggle}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            bgcolor: avatarColor(who),
            fontSize: 14,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {initials(who)}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" noWrap fontWeight={unread ? 700 : 600}>
                {who}
              </Typography>
              <Chip
                size="small"
                label={statusLabel}
                color={statusColor}
                variant={statusTone === 'default' ? 'outlined' : 'filled'}
                sx={{ height: 18, fontSize: 11, flexShrink: 0 }}
              />
              {unread && (
                <Chip
                  size="small"
                  label="Unread"
                  color="warning"
                  sx={{ height: 18, fontSize: 11 }}
                />
              )}
            </Stack>
            <Stack direction="row" spacing={0.25} alignItems="center" flexShrink={0}>
              {canReply && expanded && (
                <Tooltip title="Reply">
                  <span>
                    <IconButton
                      size="small"
                      disabled={isReplying}
                      onClick={(event) => {
                        event.stopPropagation();
                        onReply(message.historyId, false);
                      }}
                      aria-label="Reply"
                    >
                      <ReplyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {canReplyAll && expanded && (
                <Tooltip title="Reply all">
                  <span>
                    <IconButton
                      size="small"
                      disabled={isReplying}
                      onClick={(event) => {
                        event.stopPropagation();
                        onReply(message.historyId, true);
                      }}
                      aria-label="Reply all"
                    >
                      <ReplyAllIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {message.attachmentNames && (
                <Tooltip title={message.attachmentNames}>
                  <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
                </Tooltip>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {formatMessageDate(message.occurredAt)}
              </Typography>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onToggle();
                }}
                aria-label={expanded ? 'Collapse message' : 'Expand message'}
              >
                {expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Stack>
          </Stack>

          {expanded ? (
            <Stack spacing={0.2} sx={{ mt: 0.25 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: 'flex', gap: 0.75, minWidth: 0 }}
              >
                <Box component="span" sx={{ color: 'text.disabled', flexShrink: 0 }}>
                  to:
                </Box>
                <Box component="span" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                  {message.toRecipients || (isInbound ? 'me' : '—')}
                </Box>
              </Typography>
              {ccRecipients ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', gap: 0.75, minWidth: 0 }}
                >
                  <Box component="span" sx={{ color: 'text.disabled', flexShrink: 0 }}>
                    cc:
                  </Box>
                  <Box component="span" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                    {ccRecipients}
                  </Box>
                </Typography>
              ) : null}
              {bccRecipients ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', gap: 0.75, minWidth: 0 }}
                >
                  <Box component="span" sx={{ color: 'text.disabled', flexShrink: 0 }}>
                    bcc:
                  </Box>
                  <Box component="span" sx={{ minWidth: 0, wordBreak: 'break-word' }}>
                    {bccRecipients}
                  </Box>
                </Typography>
              ) : null}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.25 }}>
              {message.snippet?.trim() ||
                snippetFromBody(message.body) ||
                message.subject ||
                '(no subject)'}
            </Typography>
          )}
        </Box>
      </Stack>

      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Divider />
        <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, pl: { md: 8.5 } }}>
          <FinanceEmailCollapsibleBody
            html={sanitizedBody}
            resetKey={`${message.historyId}-${expanded ? 'open' : 'closed'}`}
          />

          {message.attachmentNames && (
            <Box sx={{ mt: 2 }}>
              <FinanceEmailAttachmentsField historyId={message.historyId} label="Attachments" />
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
